import React, { useEffect, useState } from "react";
import EmployeeLayoutV2 from "../../components/EmployeeLayoutV2";
import { getEmployeeData } from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

const EmployeeInformationV2 = () => {
    const { userData } = useAuth();
    const [formData, setFormData] = useState(null);
    const [resetMsg, setResetMsg] = useState("");
    const auth = getAuth();

    useEffect(() => {
        const fetchEmployee = async () => {
            if (!userData?.businessID || !userData?.employeeID) return;
            const data = await getEmployeeData(userData.businessID, userData.employeeID);
            setFormData(data);
        };
        fetchEmployee();
    }, [userData]);

    const handlePasswordReset = async () => {
        if (!formData || isDeactivated) return;

        setResetMsg("");
        const user = auth.currentUser;
        if (!user?.email) {
            setResetMsg("Error: No email associated with your account.");
            return;
        }

        try {
            await sendPasswordResetEmail(auth, user.email);
            setResetMsg("Password reset email sent! Check your inbox.");
        } catch (error) {
            setResetMsg("Error sending reset email: " + error.message);
        }
    };

    if (!formData) {
        return (
            <EmployeeLayoutV2>
                <div className="w-full py-8 text-sm text-gray-600">Loading...</div>
            </EmployeeLayoutV2>
        );
    }

    const isDeactivated = formData.accountStatus === "deactivated";

    return (
        <EmployeeLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

                .info-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    font-family: var(--font);
                    color: var(--text-primary);
                    width: 100%;
                    padding: 32px 0 80px;
                }
                .info-v2 .header h1 {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }
                .info-v2 .header p {
                    margin-top: 4px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                .info-v2 .surface {
                    margin-top: 22px;
                    max-width: 640px;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    padding: 24px;
                }
                .info-v2 .field { margin-bottom: 14px; }
                .info-v2 .field label {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                    margin-bottom: 6px;
                }
                .info-v2 .field input {
                    width: 100%;
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    background: var(--surface-raised);
                    color: var(--text-secondary);
                    font-size: 14px;
                    padding: 11px 12px;
                }
                .info-v2 .avatar {
                    width: 120px;
                    height: 120px;
                    border-radius: 9999px;
                    object-fit: cover;
                    border: 1px solid var(--border);
                    margin: 0 auto 18px;
                    display: block;
                }
                .info-v2 .note {
                    background: #fef6e6;
                    border: 1px solid #f0deb3;
                    color: #c47a0a;
                    font-size: 13px;
                    border-radius: 12px;
                    padding: 12px;
                    margin-top: 2px;
                }
                .info-v2 .warn {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #b91c1c;
                    font-size: 13px;
                    border-radius: 12px;
                    padding: 12px;
                    margin: 14px 0;
                }
                .info-v2 .reset-btn {
                    width: 100%;
                    margin-top: 14px;
                    background: var(--accent);
                    color: #fff;
                    border: none;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 14px;
                    padding: 11px 12px;
                    cursor: pointer;
                }
                .info-v2 .reset-btn:disabled { opacity: 0.5; cursor: not-allowed; }
                .info-v2 .reset-msg {
                    margin-top: 10px;
                    font-size: 13px;
                    color: #1d4ed8;
                }
            `}</style>

            <div className="info-v2">
                <div className="header">
                    <h1>My Information</h1>
                    <p>View your profile details and account access settings</p>
                </div>

                <div className="surface">
                    <img
                        src={
                            formData.imgUrl ||
                            "https://firebasestorage.googleapis.com/v0/b/tiply-app.firebasestorage.app/o/ChatGPT%20Image%20Apr%2010%2C%202025%2C%2002_16_58%20PM.png?alt=media&token=21d0a7dc-8eee-4341-968b-4acd8bd8e8b3"
                        }
                        alt="avatar"
                        className={`avatar ${isDeactivated ? "opacity-50" : ""}`}
                    />

                    {isDeactivated && (
                        <p className="warn">This account is deactivated. Please contact support for any inquiries.</p>
                    )}

                    {[
                        { name: "displayName", label: "Display name" },
                        { name: "firstName", label: "First name" },
                        { name: "lastName", label: "Last name" },
                        { name: "email", label: "Email" },
                        { name: "iban", label: "IBAN" },
                    ].map(({ name, label }) => (
                        <div key={name} className="field">
                            <label>{label}</label>
                            <input name={name} value={formData[name] || ""} disabled readOnly />
                        </div>
                    ))}

                    <div className="note">Please contact your manager if you wish to update your personal information.</div>

                    <button className="reset-btn" onClick={handlePasswordReset} disabled={isDeactivated}>
                        Reset Password
                    </button>

                    {resetMsg && <p className="reset-msg">{resetMsg}</p>}
                </div>
            </div>
        </EmployeeLayoutV2>
    );
};

export default EmployeeInformationV2;
